import { useDispatch } from "react-redux";
import { default as credentialsService } from "../services/credentials";
import { defineToken, undefineToken } from "../utils/token";
import { createSearchParams, useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { loginFailure, loginRequest, loginSuccess, loginNotMade } from "../actions";
import { CredentialsLoginParam, CredentialsEmailLoginConfirmParam } from "../types/credentials";
import { AxiosError } from "axios";
import useLangs from "@/languages/hooks/useLangs";
import { useSettingsContext } from "@/components/Settings/hooks/useSettingsContext";
import { useFeatureEnabled } from "@/components/ErpFeatures/hooks/useErpFeatures";
import { ErpFeatureKeys } from "@/components/ErpFeatures/models/erpFeature";

const useCredentials = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const Settings = useSettingsContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { changeLang, getBCP47Lang } = useLangs();
  const isEmailCredentialsEnabled = useFeatureEnabled(ErpFeatureKeys.FEATURE_USER_PASSWORD_EMAIL_LOGIN_API_ENDPOINT);

  // TODO: Merge with the one in AuthenticationProvider in a new hook
  const handleRedirectLogIn = (navigatePath?: string) => {
    const {
      redirectPath, pathParams, ...restSearchParams
    } = Object.fromEntries(searchParams);

    if (navigatePath) {
      navigate(t(navigatePath));
    } else if (redirectPath) {
      navigate(
        t(
          redirectPath, 
          pathParams ? JSON.parse(pathParams) : {}
        ) + createSearchParams(restSearchParams).toString()
      );
    } else {
      navigate(t(`common:url.${Settings.homepage ?? "invoices"}`));
    }
  }

  const loginCredentials = ({
    user, 
    password
  }: CredentialsLoginParam) => {
    dispatch(loginRequest("USER_AND_PASSWORD"));
    credentialsService.loginCredentials({
      user, password,
      email: Settings.logIn?.emailConfirmation && isEmailCredentialsEnabled
    })
    .then(response => {
      if ("token" in response) {
        if (response.token) {

          const { locale } = defineToken(response.token);
          if (locale) {
            changeLang(getBCP47Lang(locale));
          }
          dispatch(loginSuccess());
          if (response.change_password) {
            navigate({
              pathname: t("common:url.changePassword") as string,
              search: createSearchParams(searchParams).toString()
            });
          } else {
            handleRedirectLogIn();
          }

        } else {
          dispatch(loginFailure({
            statusCode: 401,
            status: "error"
          }));
        }
      } else {
        if (response.email_status === 'sent') {
          dispatch(loginNotMade());
          handleRedirectLogIn("common:url.email_sent");
        }
      }
    }).catch((error: AxiosError) => {
      undefineToken();
      dispatch(loginFailure({
        statusCode: 401,
        status: "error"
      }));
    });
  }
 
  const loginEmailConfirm = ({
    confirmation_token
  }: CredentialsEmailLoginConfirmParam) => {
    dispatch(loginRequest("EMAIL_USER_AND_PASSWORD"));
    credentialsService.loginEmailConfirm({confirmation_token})
    .then(response => {
      if ("token" in response) {

        const { locale } = defineToken(response.token);
        if (locale) {
          changeLang(getBCP47Lang(locale));
        }
        dispatch(loginSuccess());
        if (response.change_password) {
          navigate({
            pathname: t("common:url.changePassword") as string,
            search: createSearchParams(searchParams).toString()
          });
        } else {
          handleRedirectLogIn();
        }

      } else {
        dispatch(loginFailure({
          statusCode: 401,
          status: "error"
       }));
      }
    }).catch((error: AxiosError) => {
      undefineToken();
      dispatch(loginFailure({
        statusCode: 401,
        status: "error"
      }));
    });
  }

  return {
    loginCredentials,
    loginEmailConfirm
  }
};

export default useCredentials;
